import { ArrowRight, Clock, Eye, Medal, TrendDown, TrendUp, UsersThree, Wallet } from "@phosphor-icons/react";
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import {
  activityEnd,
  activityIsCurrent,
  activityPhase,
  activityStart,
  calculateFinanceTotals,
  collectionMeta,
  compactNumber,
  formatActivityDeadline,
  initialActivities,
  initialGuides,
  initialTransactions,
  members
} from "../data";
import { usePersistentState } from "../persistence";

function RouteLink({ to, label = "更多" }: { to: string; label?: string }) {
  return <a className="deck-link" href={`#/${to}`}>{label}<ArrowRight /></a>;
}

/** children 用来放"核心指标带"：它属于数据区第一行，不该塞进顶部的装饰封面里。 */
export function OverviewCommandDeck({ children }: { children?: ReactNode }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const powerTop = [...members].sort((a, b) => b.war.week - a.war.week).slice(0, 5);
  const contributionTop = [...members].sort((a, b) => b.contribution.week - a.contribution.week).slice(0, 5);
  const [activities] = usePersistentState("activities", initialActivities);
  const currentActivities = activities.filter((item) => activityIsCurrent(item, now));
  const featuredActivity = currentActivities.find((item) => item.featured)
    || currentActivities.find((item) => activityPhase(item, now) === "进行中")
    || currentActivities.sort((left, right) => new Date(activityStart(left)).getTime() - new Date(activityStart(right)).getTime())[0];
  const featuredPhase = featuredActivity ? activityPhase(featuredActivity, now) : null;
  const financeTotals = calculateFinanceTotals(initialTransactions);
  const rewardRows = initialTransactions.filter((item) => item.category === "军费发放").slice(0, 3);
  return (
    <section className="overview-command-deck" aria-label="联盟运营概览">
      {children}
      <div className="command-grid">
        <article className="command-panel action-summary">
          <header className="panel-head">
            <div><small>ALLIANCE ACTION</small><h2>联盟今日行动</h2></div>
            <span className={`live-chip${featuredPhase === "进行中" ? "" : " is-idle"}`}>
              <i />{featuredPhase || "暂无活动"}
            </span>
          </header>
          {featuredActivity ? (
            <>
              <div className="action-visual action-visual-data">
                <div className="action-overlay">
                  <span>{featuredActivity.type}</span>
                  <h3>{featuredActivity.title}</h3>
                  <p>{featuredActivity.description}</p>
                </div>
              </div>
              <footer className="action-footer">
                <span><Clock weight="duotone" /><b>{formatActivityDeadline(activityStart(featuredActivity))}</b><small>开始时间</small></span>
                <span><Clock weight="duotone" /><b>{activityEnd(featuredActivity) ? formatActivityDeadline(activityEnd(featuredActivity)) : "未设置"}</b><small>结束时间</small></span>
                <span><UsersThree weight="duotone" /><b>{featuredActivity.responses} / {featuredActivity.capacity}</b><small>参与人数</small></span>
                <RouteLink to="activities" label="管理活动" />
              </footer>
            </>
          ) : (
            <div className="deck-empty-state">
              <span>当前分组还没有发布活动</span>
              <small>管理端创建活动后，成员端会自动收到同一分组的数据。</small>
              <RouteLink to="activities" label="创建活动" />
            </div>
          )}
        </article>

        <article className="command-panel ranking-summary">
          <header className="panel-head">
            <div><small>WEEKLY RANKING</small><h2>本周排行</h2></div>
            <RouteLink to="rankings" />
          </header>
          <div className="dual-ranking">
            {[{ label: "战功排行", rows: powerTop, key: "war" }, { label: "贡献排行", rows: contributionTop, key: "contribution" }].map(({ label, rows, key }) => {
              // 量度条的长度按"该列最大值"归一化，让每行底部的鎏金细条直接读出量级差距
              const metric = (row: (typeof rows)[number]) => key === "war" ? row.war.week : row.contribution.week;
              const peak = rows.length ? Math.max(...rows.map(metric)) : 0;
              return (
              <section key={key}>
                <h3><Medal weight="duotone" />{label}</h3>
                {rows.length ? rows.map((row, index) => (
                  <div className="deck-rank-row" key={row.id} style={{ "--meter": `${peak > 0 ? Math.max(4, Math.round((metric(row) / peak) * 100)) : 0}%` } as CSSProperties}>
                    <span className={`rank-token r${index + 1}`}>{index + 1}</span>
                    <b>{row.name}</b>
                    <strong>{compactNumber(key === "war" ? row.war.week : row.contribution.week)}</strong>
                  </div>
                )) : <p className="deck-inline-empty">等待采集数据</p>}
              </section>
              );
            })}
          </div>
        </article>

        <article className="command-panel finance-summary">
          <header className="panel-head">
            <div><small>FINANCIAL CONTROL</small><h2>财务概览</h2></div>
            <RouteLink to="finance" />
          </header>
          <div className="finance-stack">
            <div><i className="finance-icon income"><TrendDown weight="bold" /></i><span>总收入</span><strong>{compactNumber(financeTotals.income)}</strong></div>
            <div><i className="finance-icon expense"><TrendUp weight="bold" /></i><span>总支出</span><strong>{compactNumber(financeTotals.expense)}</strong></div>
            <div><i className="finance-icon balance"><Wallet weight="fill" /></i><span>当前结余</span><strong>{compactNumber(financeTotals.balance)}</strong></div>
          </div>
          <div className={`budget-note${rewardRows.length ? "" : " is-empty"}`}>
            <small>本周军费发放</small>
            {rewardRows.length ? rewardRows.map((item) => (
              <div key={item.id}><UsersThree weight="duotone" /><span>{item.description}</span><strong>{compactNumber(item.amount)}</strong></div>
            )) : <p className="deck-inline-empty">暂无已入账的军费发放记录</p>}
          </div>
        </article>
      </div>

      <article className="command-panel guide-summary">
        <header className="panel-head">
          <div><small>KNOWLEDGE SYSTEM</small><h2>最新攻略</h2></div>
          <RouteLink to="guides" />
        </header>
        <div className="guide-strip">
          {initialGuides.length ? initialGuides.map((guide) => (
            <a href={`#/guides?article=${guide.id}`} className="guide-preview" key={guide.id}>
              <span><small>{guide.category}</small><strong>{guide.title}</strong><em><Eye />{compactNumber(guide.views)}</em></span>
            </a>
          )) : (
            <div className="deck-empty-state is-compact">
              <span>当前分组还没有发布攻略</span>
              <small>数据范围：{collectionMeta.scopeLabel}</small>
            </div>
          )}
        </div>
      </article>
    </section>
  );
}
